import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventService, invitationService, templateService } from '../services/db';
import Header from '../components/Header';
import './HomePage.css';
import PageTitle from '../components/PageTitle';

function HomePage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!currentUser) {
          navigate('/login');
          return;
        }
        
        setLoading(true);
        
        // Load events
        const userEvents = await eventService.getAll(currentUser.id);
        setEvents(userEvents || []);
        
        // Load recent invitations
        // We'll need to get invitations across all events
        let allInvitations = [];
        for (const event of userEvents.slice(0, 5)) { // Limit to avoid too many requests
          const eventInvitations = await invitationService.getByEventId(event.id);
          allInvitations = [...allInvitations, ...eventInvitations];
        }
        
        // Sort by most recent and take top 5
        allInvitations.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setInvitations(allInvitations.slice(0, 5));
        
        // Load templates
        const userTemplates = await templateService.getAll(currentUser.id);
        setTemplates(userTemplates || []);
        
        setLoading(false);
      } catch (error) {
        console.error("Error loading home page data:", error);
        setLoading(false);
      }
    };
    
    loadData();
  }, [currentUser, navigate]);
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="home-page">
      <PageTitle title="Home" />
      <Header />
      
      <div className="dashboard-container">
        {/* Hero section with welcome message */}
        <section className="welcome-section">
          <div className="welcome-content">
            <h1>Welcome, {currentUser?.fullName || currentUser?.username || 'there'}!</h1>
            <p className="welcome-subtitle">Your digital invitation dashboard</p>
          </div>
          <div className="welcome-image">
            <img src="/images/icon.png" alt="Wedding App Icon" />
          </div>
        </section>
        
        {/* Main content */}
        <div className="dashboard-content">
          {/* Events section */}
          <section className="dashboard-section events-section">
            <div className="section-header">
              <h2>My Events</h2>
              <Link to="/events" className="view-all-link">
                View All
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
            
            <div className="section-content">
              {loading ? (
                <div className="loading-placeholder">
                  <div className="loading-card"></div>
                  <div className="loading-card"></div>
                </div>
              ) : events.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <p>No events yet</p>
                  <Link to="/events/new" className="create-new-btn">Create Event</Link>
                </div>
              ) : (
                <div className="events-grid">
                  {events.slice(0, 4).map(event => (
                    <Link to={`/events/${event.id}`} key={event.id} className="event-card">
                      <div className="event-card-content">
                        <h3>{event.name}</h3>
                        <div className="event-meta">
                          <span className="event-date">{formatDate(event.date)}</span>
                          <span className="event-location">
                            {event.location && typeof event.location === 'object' 
                              ? (event.location.city ? `${event.location.city}${event.location.state ? `, ${event.location.state}` : ''}` : 
                                 (event.location.venue || 'Location not specified')) 
                              : (typeof event.location === 'string' ? event.location : 'Location not specified')}
                          </span>
                        </div>
                      </div>
                      <div className="card-indicator">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </Link>
                  ))}
                  
                  <Link to="/events/new" className="create-card">
                    <div className="create-card-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <span>New Event</span>
                  </Link>
                </div>
              )}
            </div>
          </section>
          
          {/* Invitations section */}
          <section className="dashboard-section invitations-section">
            <div className="section-header">
              <h2>Recent Invitations</h2>
              <Link to="/events" className="view-all-link">
                View All
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
            
            <div className="section-content">
              {loading ? (
                <div className="loading-placeholder">
                  <div className="loading-card"></div>
                  <div className="loading-card"></div>
                </div>
              ) : invitations.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 10L12 15L21 10" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <p>No invitations yet</p>
                  {events.length > 0 && (
                    <Link to={`/events/${events[0].id}`} className="create-new-btn">Create Invitation</Link>
                  )}
                </div>
              ) : (
                <div className="invitations-list">
                  {invitations.map(invitation => {
                    // Find the parent event
                    const parentEvent = events.find(e => e.id === invitation.eventId);
                    
                    return (
                      <Link 
                        to={`/events/${invitation.eventId}/invitation/${invitation.id}/edit`} 
                        key={invitation.id} 
                        className="invitation-card"
                      >
                        <div className="invitation-content">
                          <h3>{invitation.name}</h3>
                          {parentEvent && (
                            <p className="invitation-event">{parentEvent.name}</p>
                          )}
                        </div>
                        <div className="invitation-status">
                          {invitation.published ? (
                            <span className="status published">Published</span>
                          ) : (
                            <span className="status draft">Draft</span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
          
          {/* Templates section */}
          <section className="dashboard-section templates-section">
            <div className="section-header">
              <h2>Templates</h2>
              <Link to="/templates" className="view-all-link">
                View All
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
            
            <div className="section-content">
              {loading ? (
                <div className="template-grid loading-placeholder">
                  <div className="loading-card"></div>
                  <div className="loading-card"></div>
                  <div className="loading-card"></div>
                </div>
              ) : templates.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M7 9H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M7 13H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M7 17H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <p>No templates yet</p>
                  <Link to="/editor" className="create-new-btn">Create Template</Link>
                </div>
              ) : (
                <div className="template-grid">
                  {templates.slice(0, 3).map(template => (
                    <div key={template.id} className="template-card">
                      <div className="template-preview">
                        {template.thumbnail ? (
                          <img src={template.thumbnail} alt={template.name} />
                        ) : (
                          <div className="template-placeholder">
                            <span>{template.name.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className="template-info">
                        <h3>{template.name}</h3>
                        <button onClick={() => navigate(`/editor/${template.id}`)} className="use-template-btn">
                          Use Template
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  <Link to="/editor" className="create-template-card">
                    <div className="create-card-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <span>New Template</span>
                  </Link>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default HomePage; 